/**
 * SupabaseLossReasonsRepository
 * Responsabilidade: Acesso a dados de motivos de perda no Supabase
 * Princípios: Single Responsibility, Dependency Inversion
 */

import { supabase } from '../../lib/supabase';
import { LossReason } from '../../types';

export class SupabaseLossReasonsRepository {
    async findAll(organizationId: string): Promise<LossReason[]> {
        const { data, error } = await supabase
            .from('loss_reasons')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[LossReasonsRepository] Error fetching loss reasons:', error);
            throw new Error(`Erro ao buscar motivos de perda: ${error.message}`);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string, organizationId: string): Promise<LossReason | null> {
        const { data, error } = await supabase
            .from('loss_reasons')
            .select('*')
            .eq('id', id)
            .eq('organization_id', organizationId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            console.error('[LossReasonsRepository] Error fetching loss reason:', error);
            throw new Error(`Erro ao buscar motivo de perda: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async create(lossReason: Omit<LossReason, 'id' | 'createdAt'>, organizationId: string): Promise<LossReason> {
        const { data, error } = await supabase
            .from('loss_reasons')
            .insert([{
                organization_id: organizationId,
                name: lossReason.name
            }])
            .select()
            .single();

        if (error) {
            console.error('[LossReasonsRepository] Error creating loss reason:', error);
            throw new Error(`Erro ao criar motivo de perda: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async update(id: string, lossReason: Partial<LossReason>, organizationId: string): Promise<LossReason> {
        const updateData: any = {};
        if (lossReason.name !== undefined) updateData.name = lossReason.name;

        const { data, error } = await supabase
            .from('loss_reasons')
            .update(updateData)
            .eq('id', id)
            .eq('organization_id', organizationId)
            .select()
            .single();

        if (error) {
            console.error('[LossReasonsRepository] Error updating loss reason:', error);
            throw new Error(`Erro ao atualizar motivo de perda: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async delete(id: string, organizationId: string): Promise<void> {
        await this.clearOpportunityReferences([id], organizationId);

        const { error } = await supabase
            .from('loss_reasons')
            .delete()
            .eq('id', id)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[LossReasonsRepository] Error deleting loss reason:', error);
            throw new Error(`Erro ao deletar motivo de perda: ${error.message}`);
        }
    }

    async deleteBulk(ids: string[], organizationId: string): Promise<void> {
        await this.clearOpportunityReferences(ids, organizationId);

        const { error } = await supabase
            .from('loss_reasons')
            .delete()
            .in('id', ids)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[LossReasonsRepository] Error deleting loss reasons in bulk:', error);
            throw new Error(`Erro ao deletar motivos de perda: ${error.message}`);
        }
    }

    private async clearOpportunityReferences(ids: string[], organizationId: string): Promise<void> {
        if (ids.length === 0) return;

        const { error } = await supabase
            .from('opportunities')
            .update({ lost_reason_id: null, lost_reason: null })
            .in('lost_reason_id', ids)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[LossReasonsRepository] Error clearing loss reason references:', error);
            throw new Error(`Erro ao desvincular motivos de perda: ${error.message}`);
        }
    }

    private mapToEntity(data: any): LossReason {
        return {
            id: data.id,
            name: data.name,
            createdAt: data.created_at
        };
    }
}

export const lossReasonsRepository = new SupabaseLossReasonsRepository();
